// OnDutyApprovalIndex.jsx
import React, { useEffect, useState } from "react";
import {
  Grid,
  CssBaseline,
  AppBar,
  Toolbar,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  TextField,
  TableContainer,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { Close, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import AddUpdateOnDutyApprovalView from "./AddUpdateOnDutyApprovalView";
import dayjs from "dayjs";

const OnDutyApprovalIndex = ({ handleBackWinderMachineEntry }) => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  /* ---------- Menu Filter ---------- */
  const [selectedOption, setSelectedOption] = useState("All Entries");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuItemClick = (option) => {
    setSelectedOption(option);
    handleMenuClose();
  };

  /* ---------- Column Filters ---------- */
  const [filters, setFilters] = useState({
    id_on_duty: "",
    id_employee: "",
    travel_purpose: "",
    location: "",
    on_duty_from_date: "",
    on_duty_to_date: "",
    approved_by: "",
    billing_id: "",
    company_id: "",
    plant_id: "",
    status: "",
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /* ---------- Load List ---------- */
  const loadList = () => {
    const payload = {
      p_id_employee: 3,
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
      p_id_on_duty: 0,
    };

    callApi(
      API_ENDPOINTS.onduty?.Approvallist || "/api/onduty/Approvallist",
      "POST",
      { "Content-Type": "application/json" },
      payload
    )
      .then((data) => {
        setList(data || []);
        setFiltered(data || []);
      })
      .catch(() => {
        setList([]);
        setFiltered([]);
      });
  };

  useEffect(() => {
    loadList();
  }, []);

  /* ---------- Apply Filters ---------- */
  useEffect(() => {
    let data = [...list];

    // Menu filter (STATUS IS STRING)
    if (selectedOption === "Approved Entries") {
      data = data.filter(
        (x) => x.status?.toLowerCase() === "approved"
      );
    }

    if (selectedOption === "Pending Entries") {
      data = data.filter(
        (x) => x.status?.toLowerCase() === "pending"
      );
    }

    // Column-wise filter
    data = data.filter((row) =>
      Object.keys(filters).every((key) => {
        if (!filters[key]) return true;
        return String(row[key] ?? "")
          .toLowerCase()
          .includes(filters[key].toLowerCase());
      })
    );

    setFiltered(data);
  }, [list, filters, selectedOption]);

  /* ---------- Edit ---------- */
  const handleEdit = async (row) => {
    const payload = {
      p_id_on_duty: row.id_on_duty,
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
      p_id_employee: row.id_employee,
    };

    const detailData = await callApi(
      API_ENDPOINTS.onduty.ApprovallistgetById,
      "POST",
      { "Content-Type": "application/json" },
      payload
    );

    setFormData(detailData);
    setIsFormOpen(true);
  };

  const handleExit = () => {
    handleBackWinderMachineEntry();
  };

  return (
    <Grid>
      {!isFormOpen && (
        <>
          <CssBaseline />

          {/* ---------- HEADER ---------- */}
          <AppBar
            position="static"
            sx={{
              backgroundColor: "inherit",
              height: 48,
              justifyContent: "center",
            }}
          >
            <Toolbar>
              <Typography
                variant="h6"
                // sx={{ color: "#fff", fontWeight: 500 }}
              >
                On Duty / Travel Requests
              </Typography>

              {/* ---------- MENU ---------- */}
             <Button
             color="secondary"
  onClick={handleMenuClick}
  endIcon={
   <ExpandMoreIcon sx={{ color: "teal", fontSize: "18px" }} />
  }
 sx={{
    ml: 2,
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 500,
  }}
>
  {selectedOption}
</Button>


              <Menu
  anchorEl={anchorEl}
  open={openMenu}
  onClose={handleMenuClose}
>
  {["All Entries", "Approved Entries", "Pending Entries"].map((opt) => (
    <MenuItem
      key={opt}
      onClick={() => handleMenuItemClick(opt)}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      {opt}
    </MenuItem>
  ))}
</Menu>


              <IconButton onClick={handleExit} sx={{ ml: "auto", color: "#fff" }}>
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* ---------- TABLE ---------- */}
          <Box
            p={1}
            sx={{
              "& .MuiTableCell-root": { whiteSpace: "nowrap" },
              maxHeight: "75vh",
              overflow: "auto",
            }}
          >
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>On Duty ID</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Billing ID</TableCell>
                    <TableCell>Company ID</TableCell>
                    <TableCell>Plant ID</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>

                  <TableRow>
                    {Object.keys(filters).map((col) => (
                      <TableCell key={col}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search"
                          value={filters[col]}
                          onChange={(e) =>
                            handleFilterChange(col, e.target.value)
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id_on_duty}>
                      <TableCell
                        sx={{ color: "teal", cursor: "pointer" }}
                        onClick={() => handleEdit(row)}
                      >
                        {row.id_on_duty}
                      </TableCell>
                      <TableCell>{row.id_employee}</TableCell>
                      <TableCell>{row.travel_purpose}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      {/* <TableCell>{row.on_duty_from_date}</TableCell>
                      <TableCell>{row.on_duty_to_date}</TableCell> */}
                      <TableCell>
  {row.on_duty_from_date
    ? dayjs(row.on_duty_from_date).format("DD-MMM-YYYY")
    : "-"}
</TableCell>

<TableCell>
  {row.on_duty_to_date
    ? dayjs(row.on_duty_to_date).format("DD-MMM-YYYY")
    : "-"}
</TableCell>
                      <TableCell>{row.approved_by}</TableCell>
                      <TableCell>{row.billing_id}</TableCell>
                      <TableCell>{row.company_id}</TableCell>
                      <TableCell>{row.plant_id}</TableCell>
                      <TableCell>{row.status || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {isFormOpen && (
        <AddUpdateOnDutyApprovalView
          formData={formData}
          setIsFormOpen={setIsFormOpen}
          reloadList={loadList}
        />
      )}
    </Grid>
  );
};

export default OnDutyApprovalIndex;
